/**
 * The dashboard list — REQ-401, and the filters REQ-402 asks for.
 *
 * ## What a row carries, and what it cannot
 *
 * REQ-401 names three things per row: current state, last check time, and
 * recent response time. Two of those are on the `Monitor` schema and are
 * rendered here. **The third is not in the contract at all.** `Monitor` has no
 * response-time field; the only `duration_ms` in the document belongs to
 * `CheckExecution`, which is the answer to `POST /monitors/{id}/check` and not
 * something `GET /monitors` returns. The stored `response_time_ms` is mentioned
 * in a description as a column that exists in the database and is exposed by no
 * response schema.
 *
 * So the column is absent and the list says so, rather than a plausible number
 * appearing where the contract provides none. That is the standing rule for
 * this repo: build against the contract, and when the contract is short of
 * something, report it as a cross-project request instead of guessing a field
 * name that will typecheck against nothing.
 *
 * ## Ordering
 *
 * The API's order is preserved exactly. §2: Postgres orders the state enum by
 * declaration order, so the server's `ORDER BY state DESC` already floats every
 * "no current evidence" state above `up` — the states a NOC needs to see reach
 * the top without anyone remembering to sort. Filtering below removes rows; it
 * never reorders them.
 *
 * ## Filtering
 *
 * `GET /monitors` takes no query parameters, so the three axes REQ-402 names are
 * applied client-side over the returned list. Two of them — `state` and `tags` —
 * are fields on `Monitor` and are filtered on directly. The third,
 * organisation, is not a filter at this layer at all: scope is the
 * `X-Halyard-Organisation` header and is chosen in the header bar, because the
 * API returns one organisation's monitors and no others (REQ-703). A filter
 * that appeared to select across organisations would be a lie about what the
 * response contains.
 *
 * The state filter offers all five labels. §4.4 is explicit that a
 * "healthy / unhealthy" pair instead of the five fails REQ-402 and §2 both.
 */
import { useMemo, useState } from "react";

import { MONITOR_STATES, monitorStateFacts } from "../api/index.ts";
import type { MonitorBody, MonitorState } from "../api/index.ts";
import { STATE_COLOURS } from "../theme/state-colours.ts";
import { StateChip } from "./StateChip.tsx";

export interface MonitorListProps {
  readonly monitors: readonly MonitorBody[];
  /** `MonitorList.total` — the organisation's count, which is not `monitors.length`. */
  readonly total: number;
}

/**
 * A timestamp a person can read, with the raw value on hover.
 *
 * `last_result_at` is nullable and null means **no result has ever been
 * recorded**, which ACC-2 is explicit is not health. It renders as the words
 * "Never checked" rather than an em-dash: a dash in a table reads as "nothing
 * to say here", and this is the opposite — it is the most important thing the
 * row has to say.
 */
function Timestamp({ value }: { readonly value: string | null }): React.JSX.Element {
  if (value === null) {
    return <span className="timestamp timestamp--never">Never checked</span>;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return <span className="timestamp">{value}</span>;
  }

  return (
    <time className="timestamp" dateTime={value} title={value}>
      {parsed.toLocaleString()}
    </time>
  );
}

export function MonitorList({ monitors, total }: MonitorListProps): React.JSX.Element {
  const [stateFilter, setStateFilter] = useState<MonitorState | "all">("all");
  const [tagFilter, setTagFilter] = useState<string>("all");
  const [search, setSearch] = useState("");

  const tags = useMemo(() => {
    const seen = new Set<string>();
    for (const monitor of monitors) for (const tag of monitor.tags) seen.add(tag);
    return [...seen].sort((a, b) => a.localeCompare(b));
  }, [monitors]);

  // How many monitors sit in each state, for the filter buttons. Counted from
  // the full list rather than the filtered one, so the numbers do not move
  // around as someone clicks through them.
  const countByState = useMemo(() => {
    const counts = new Map<MonitorState, number>();
    for (const state of MONITOR_STATES) counts.set(state, 0);
    for (const monitor of monitors) counts.set(monitor.state, (counts.get(monitor.state) ?? 0) + 1);
    return counts;
  }, [monitors]);

  const visible = useMemo(() => {
    const needle = search.trim().toLowerCase();
    // `filter` preserves order, which is the point — see the note at the top.
    return monitors.filter((monitor) => {
      if (stateFilter !== "all" && monitor.state !== stateFilter) return false;
      if (tagFilter !== "all" && !monitor.tags.includes(tagFilter)) return false;
      if (needle === "") return true;
      return (
        monitor.name.toLowerCase().includes(needle) || monitor.target.toLowerCase().includes(needle)
      );
    });
  }, [monitors, stateFilter, tagFilter, search]);

  return (
    <section className="list">
      <div className="list__toolbar">
        <div className="filters" role="group" aria-label="Filter by state">
          <button
            type="button"
            className={`filter ${stateFilter === "all" ? "filter--on" : ""}`}
            onClick={() => {
              setStateFilter("all");
            }}
          >
            All <span className="filter__count">{monitors.length}</span>
          </button>
          {MONITOR_STATES.map((state) => (
            <button
              key={state}
              type="button"
              title={monitorStateFacts(state).meaning}
              className={`filter ${stateFilter === state ? "filter--on" : ""}`}
              style={
                stateFilter === state
                  ? { color: STATE_COLOURS[state].hex, borderColor: STATE_COLOURS[state].hex }
                  : undefined
              }
              onClick={() => {
                setStateFilter(state);
              }}
            >
              <span aria-hidden="true" style={{ color: STATE_COLOURS[state].hex }}>
                {STATE_COLOURS[state].glyph}
              </span>{" "}
              {monitorStateFacts(state).label}{" "}
              <span className="filter__count">{countByState.get(state) ?? 0}</span>
            </button>
          ))}
        </div>

        <div className="filters filters--right">
          <label className="field field--inline">
            <span className="field__label">Tag</span>
            <select
              className="field__input"
              value={tagFilter}
              onChange={(event) => {
                setTagFilter(event.target.value);
              }}
            >
              <option value="all">All tags</option>
              {tags.map((tag) => (
                <option key={tag} value={tag}>
                  {tag}
                </option>
              ))}
            </select>
          </label>

          <label className="field field--inline">
            <span className="field__label">Search</span>
            <input
              className="field__input"
              type="search"
              placeholder="Name or target"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
              }}
            />
          </label>
        </div>
      </div>

      <p className="list__count">
        Showing {visible.length} of {monitors.length} monitor{monitors.length === 1 ? "" : "s"}.{" "}
        {/* `total` is the organisation's count and is reported separately from the
            array's length on purpose: they are the two halves of ACC-2's
            distinction between "no monitors exist" and "the list came back empty". */}
        <span className="list__total">The API reports {total} in this organisation.</span>
      </p>

      {visible.length === 0 ? (
        <p className="list__none">No monitor matches these filters. Clear them to see the rest.</p>
      ) : (
        <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">State</th>
                <th scope="col">Monitor</th>
                <th scope="col">Kind</th>
                <th scope="col">Target</th>
                <th scope="col">Last check</th>
                <th scope="col">Next expected</th>
                <th scope="col">Consecutive failures</th>
                <th scope="col">Tags</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((monitor) => (
                <tr key={monitor.id}>
                  <td>
                    <StateChip state={monitor.state} />
                  </td>
                  <td className="cell--name">{monitor.name}</td>
                  <td>
                    <code>{monitor.kind}</code>
                  </td>
                  <td className="cell--target" title={monitor.target}>
                    {monitor.target}
                  </td>
                  <td>
                    <Timestamp value={monitor.last_result_at} />
                  </td>
                  <td>
                    <Timestamp value={monitor.expected_next_check_at} />
                  </td>
                  <td className="cell--number">{monitor.consecutive_failures}</td>
                  <td>
                    {monitor.tags.length === 0 ? (
                      <span className="muted">none</span>
                    ) : (
                      monitor.tags.map((tag) => (
                        <span key={tag} className="tag">
                          {tag}
                        </span>
                      ))
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="list__footnote">
        REQ-401 also asks for a recent response time per monitor. The vendored contract does not
        carry one: <code>Monitor</code> has no response-time field, and the only{" "}
        <code>duration_ms</code> in the document belongs to <code>CheckExecution</code>, which is the
        answer to a manual check rather than anything <code>GET /monitors</code> returns. The column
        is absent rather than invented, and the gap is filed as a request to halyard-monitor-api.
      </p>
    </section>
  );
}
